// Scholar Sync API Service
import { Paper } from "../types/paper";

// Backend paper structure from Scholar Sync
interface BackendPaper {
  id: number;
  user_email: string;
  title: string;
  authors: string; // Comma-separated string
  publication_date: string; // ISO date string
  clarity: number;
  usefulness: number;
  impact: number;
  comment: string;
  citation: string;
  url: string;
  reading_list: string;
  date_accessed: string;
  is_important?: boolean;  // Scholar Sync uses this for starred papers
  starred?: boolean;
  is_starred?: boolean;  // Alternative field name
  favorite?: boolean;    // Alternative field name
  // Enhanced metadata
  abstract?: string;
  keywords?: string;
  journal?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  doi?: string;
  issn?: string;
  publisher?: string;
  language?: string;
  article_type?: string;
  access_date?: string;
}

class ScholarSyncAPI {
  private baseURL = "https://web-production-2564.up.railway.app";
  private userEmail: string | null = null;

  /**
   * Set the user email for API requests
   */
  setUserEmail(email: string): void {
    this.userEmail = email;
    // Store in localStorage for persistence
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("scholarsync_user_email", email);
    }
  }

  /**
   * Get stored user email
   */
  getUserEmail(): string | null {
    if (!this.userEmail && typeof localStorage !== "undefined") {
      this.userEmail = localStorage.getItem("scholarsync_user_email");
    }
    return this.userEmail;
  }

  /**
   * Clear user email (logout)
   */
  clearUserEmail(): void {
    this.userEmail = null;
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("scholarsync_user_email");
    }
  }

  /**
   * Fetch all papers for the current user
   */
  async getPapers(readingList?: string): Promise<Paper[]> {
    const email = this.getUserEmail();
    if (!email) {
      throw new Error("User email not set. Please log in.");
    }

    try {
      const url = new URL(`${this.baseURL}/papers`);
      url.searchParams.append("user_email", email);
      if (readingList) {
        url.searchParams.append("reading_list", readingList);
      }

      console.log(`Fetching papers from: ${url.toString()}`);
      const response = await fetch(url.toString());

      console.log(`Response status: ${response.status}`);
      console.log(`Response ok: ${response.ok}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP Error ${response.status}: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const responseText = await response.text();
      console.log(`Response body length: ${responseText.length}`);
      console.log(`Response preview: ${responseText.substring(0, 200)}`);

      const responseData = JSON.parse(responseText);
      
      // Backend returns {papers: [...]} instead of just [...]
      const backendPapers: BackendPaper[] = responseData.papers || responseData;
      
      console.log(`Parsed ${backendPapers.length} papers from backend`);
      
      const transformedPapers = backendPapers.map(this.transformPaper);
      console.log(`Transformed papers:`, transformedPapers);
      
      return transformedPapers;
    } catch (error) {
      console.error("Error fetching papers:", error);
      if (error instanceof Error) {
        console.error("Error name:", error.name);
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }
      throw new Error(`Failed to fetch papers: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Get reading lists for the current user
   */
  async getReadingLists(): Promise<string[]> {
    const email = this.getUserEmail();
    if (!email) {
      throw new Error("User email not set. Please log in.");
    }

    try {
      const response = await fetch(`${this.baseURL}/reading_lists/${email}`);

      if (!response.ok) {
        console.warn(`Reading lists endpoint returned ${response.status}, returning empty array`);
        return [];
      }

      const data = await response.json();
      console.log("Reading lists response:", data);
      
      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      } else if (data.reading_lists && Array.isArray(data.reading_lists)) {
        return data.reading_lists;
      } else if (typeof data === 'object' && data !== null) {
        // If it's an object, try to extract array values
        return Object.values(data).filter(v => typeof v === 'string');
      }
      
      console.warn("Unexpected reading lists format:", data);
      return [];
    } catch (error) {
      console.error("Error fetching reading lists:", error);
      return [];
    }
  }

  /**
   * Client-side search through papers
   */
  searchPapers(papers: Paper[], query: string): Paper[] {
    if (!query || query.trim() === "") {
      return papers;
    }

    const lowerQuery = query.toLowerCase();

    return papers.filter((paper) => {
      // Search in title
      if (paper.title.toLowerCase().includes(lowerQuery)) {
        return true;
      }

      // Search in authors
      if (paper.authors.some((author) => author.toLowerCase().includes(lowerQuery))) {
        return true;
      }

      // Search in abstract
      if (paper.abstract && paper.abstract.toLowerCase().includes(lowerQuery)) {
        return true;
      }

      // Search in journal
      if (paper.journal && paper.journal.toLowerCase().includes(lowerQuery)) {
        return true;
      }

      // Search in year
      if (paper.year.toString().includes(query)) {
        return true;
      }

      return false;
    });
  }

  /**
   * Transform backend paper format to frontend format
   */
  private transformPaper(backendPaper: BackendPaper): Paper {
    try {
      // Parse authors from comma-separated string
      const authors = backendPaper.authors
        ? backendPaper.authors.split(",").map((a) => a.trim()).filter((a) => a.length > 0)
        : ["Unknown Author"];

      // Extract year from publication_date
      let year = new Date().getFullYear();
      if (backendPaper.publication_date) {
        try {
          const parsedYear = new Date(backendPaper.publication_date).getFullYear();
          if (!isNaN(parsedYear) && parsedYear > 1900 && parsedYear <= new Date().getFullYear() + 10) {
            year = parsedYear;
          }
        } catch (e) {
          console.warn(`Failed to parse year from ${backendPaper.publication_date}, using current year`);
        }
      }

      // Check for starred status using multiple possible field names
      // Scholar Sync uses 'is_important' for starred papers
      const isStarred = backendPaper.is_important || backendPaper.starred || backendPaper.is_starred || backendPaper.favorite || false;
      
      console.log(`Paper starred status:`, {
        id: backendPaper.id,
        title: backendPaper.title?.substring(0, 50),
        is_important: backendPaper.is_important,
        starred: backendPaper.starred,
        is_starred: backendPaper.is_starred,
        favorite: backendPaper.favorite,
        finalStarred: isStarred
      });

      const paper: Paper = {
        id: backendPaper.id.toString(),
        title: backendPaper.title || "Untitled",
        authors: authors,
        year: year,
        journal: backendPaper.journal || undefined,
        volume: backendPaper.volume || undefined,
        issue: backendPaper.issue || undefined,
        pages: backendPaper.pages || undefined,
        doi: backendPaper.doi || undefined,
        url: backendPaper.url || undefined,
        abstract: backendPaper.abstract || undefined,
        starred: isStarred,
        dateAccessed: backendPaper.date_accessed || undefined,
        clarity: backendPaper.clarity || undefined,
        usefulness: backendPaper.usefulness || undefined,
        impact: backendPaper.impact || undefined,
      };

      console.log(`Transformed paper:`, {
        id: paper.id,
        title: paper.title.substring(0, 50),
        authors: paper.authors,
        year: paper.year
      });

      return paper;
    } catch (error) {
      console.error("Error transforming paper:", backendPaper, error);
      // Return a fallback paper instead of crashing
      return {
        id: backendPaper.id?.toString() || "unknown",
        title: backendPaper.title || "Error loading paper",
        authors: ["Unknown"],
        year: new Date().getFullYear(),
      };
    }
  }

  /**
   * Verify user credentials (for login)
   */
  async login(email: string, pin: string): Promise<boolean> {
    try {
      console.log(`Attempting login for: ${email}`);
      
      const response = await fetch(`${this.baseURL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, pin }),
      });

      console.log(`Login response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Login failed: ${response.status} - ${errorText}`);
        
        // If endpoint doesn't exist, try to fetch papers directly to verify email
        if (response.status === 404) {
          console.log("Login endpoint not found, trying direct paper fetch...");
          try {
            const testUrl = `${this.baseURL}/papers?user_email=${encodeURIComponent(email)}`;
            const testResponse = await fetch(testUrl);
            if (testResponse.ok) {
              const papers = await testResponse.json();
              console.log(`Found ${papers.length} papers for user, treating as successful login`);
              this.setUserEmail(email);
              return true;
            }
          } catch (e) {
            console.error("Paper fetch test failed:", e);
          }
        }
        
        return false;
      }

      const data = await response.json();
      console.log("Login response:", data);

      if (data.success) {
        this.setUserEmail(email);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Login error:", error);
      
      // Try alternative: just set email and test if papers endpoint works
      console.log("Attempting fallback authentication...");
      try {
        const testUrl = `${this.baseURL}/papers?user_email=${encodeURIComponent(email)}`;
        const testResponse = await fetch(testUrl);
        if (testResponse.ok) {
          const papers = await testResponse.json();
          console.log(`Fallback successful: Found ${papers.length} papers`);
          this.setUserEmail(email);
          return true;
        }
      } catch (e) {
        console.error("Fallback authentication failed:", e);
      }
      
      return false;
    }
  }
}

// Export singleton instance
export const scholarSyncAPI = new ScholarSyncAPI();
